import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Shield, Lock, User, ArrowLeft, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLogin = () => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/admin/login`, credentials);
      localStorage.setItem('admin_token', response.data.token);
      navigate('/proadmin/dashboard');
    } catch (err) {
      setError('Geçersiz kullanıcı adı veya şifre');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] flex">
      {/* Sol Panel - Bilgi (Desktop) */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#006D77] to-[#004d54] p-12 flex-col justify-between relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10">
          <img src="/logo-white.png" alt="HizmetGeliyo" className="h-12 mb-12" onError={(e) => e.target.style.display='none'} />
          <h1 className="text-4xl font-bold text-white mb-4">
            Yönetim Paneli
          </h1>
          <p className="text-white/80 text-lg mb-8">
            HizmetGeliyo platformunu yönetin, istatistikleri takip edin.
          </p>
          
          <div className="space-y-4">
            <div className="flex items-center gap-3 text-white/90">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Shield className="w-5 h-5" />
              </div>
              <span>Güvenli admin erişimi</span>
            </div>
            <div className="flex items-center gap-3 text-white/90">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <User className="w-5 h-5" />
              </div>
              <span>Usta ve müşteri yönetimi</span>
            </div>
          </div>
        </div>
        
        <p className="text-white/60 text-sm relative z-10">
          © 2026 HizmetGeliyo. Tüm hakları saklıdır.
        </p>
      </div>

      {/* Sağ Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Mobil Logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <img 
              src="/logo-transparent.png" 
              alt="HizmetGeliyo" 
              className="h-10 w-auto object-contain"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/logo.png';
              }}
            />
          </div>

          <div className="bg-[#1e293b]/80 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/10 p-8">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-[#006D77] to-[#004d54] rounded-2xl flex items-center justify-center shadow-lg shadow-[#006D77]/20">
                <Shield className="w-8 h-8 text-white" />
              </div>
            </div>

            <h2 className="text-2xl font-bold text-white text-center mb-2">Admin Girişi</h2>
            <p className="text-white/50 text-center mb-6">Yönetim paneline erişin</p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="username" className="text-white/70 text-sm">
                  Kullanıcı Adı
                </Label>
                <div className="relative mt-2">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <Input
                    id="username"
                    value={credentials.username}
                    onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                    className="pl-10 h-12 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#006D77] focus:ring-[#006D77]"
                    placeholder="admin"
                    required
                    data-testid="admin-username"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-white/70 text-sm">
                  Şifre
                </Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <Input
                    id="password"
                    type="password"
                    value={credentials.password}
                    onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                    className="pl-10 h-12 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#006D77] focus:ring-[#006D77]"
                    placeholder="••••••••"
                    required
                    data-testid="admin-password"
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-gradient-to-r from-[#006D77] to-[#005a63] hover:from-[#005a63] hover:to-[#004d54] text-white font-medium"
                data-testid="admin-login-button"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : 'Giriş Yap'}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-white/10 text-center">
              <button
                onClick={() => navigate('/')}
                className="text-white/50 hover:text-white text-sm flex items-center justify-center gap-1 mx-auto transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Ana Sayfaya Dön
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
